import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Download, ChevronDown, ChevronRight, Plane, Calendar, X, Upload } from 'lucide-react';

export default function Missing7hrReport() {
  const today = new Date();
  
  // Load saved filters from localStorage
  const loadFilters = () => {
    try {
      const saved = localStorage.getItem('missing7hrFilters');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  };
  
  const savedFilters = loadFilters();
  
  // Helper to format date in local timezone (YYYY-MM-DD)
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Calculate default date range (current month)
  const getDefaultStartDate = () => {
    const date = new Date(today.getFullYear(), today.getMonth(), 1);
    return formatLocalDate(date);
  };
  const getDefaultEndDate = () => {
    const date = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return formatLocalDate(date);
  };
  
  const [dateRangeMode, setDateRangeMode] = useState(savedFilters.dateRangeMode || 'month');
  const [year, setYear] = useState(savedFilters.year || today.getFullYear());
  const [month, setMonth] = useState(savedFilters.month || today.getMonth() + 1);
  const [startDate, setStartDate] = useState(savedFilters.startDate || getDefaultStartDate());
  const [endDate, setEndDate] = useState(savedFilters.endDate || getDefaultEndDate());
  const [threshold, setThreshold] = useState(savedFilters.threshold || 7);
  const [workdaysOnly, setWorkdaysOnly] = useState(savedFilters.workdaysOnly !== false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState(savedFilters.selectedGroupIds || []);
  const [filterByAdmin, setFilterByAdmin] = useState(savedFilters.filterByAdmin !== false);
  const [filterByMember, setFilterByMember] = useState(savedFilters.filterByMember !== false);
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState(savedFilters.selectedUserIds || []);
  const [showVacationModal, setShowVacationModal] = useState(false);
  const [vacationUserId, setVacationUserId] = useState(null);
  const [vacationUserName, setVacationUserName] = useState('');
  const [vacationDays, setVacationDays] = useState({});
  const [userThresholds, setUserThresholds] = useState({});
  const [editingThresholdUserId, setEditingThresholdUserId] = useState(null);
  const [changeHistory, setChangeHistory] = useState([]);
  const [showChangeHistory, setShowChangeHistory] = useState(false);
  const fileInputRef = useRef(null);

  // Load vacation days from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('userVacationDays');
    if (saved) {
      try {
        setVacationDays(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load vacation days:', e);
      }
    }

    const savedThresholds = localStorage.getItem('userThresholds');
    if (savedThresholds) {
      try {
        setUserThresholds(JSON.parse(savedThresholds));
      } catch (e) {
        console.error('Failed to load user thresholds:', e);
      }
    }

    const savedHistory = localStorage.getItem('missing7hrChangeHistory');
    if (savedHistory) {
      try {
        setChangeHistory(JSON.parse(savedHistory));
      } catch (e) {
        console.error('Failed to load change history:', e);
      }
    }
  }, []);

  // Add change to history
  const logChange = (type, description, details = {}) => {
    const change = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      type, // 'vacation', 'threshold', 'filter'
      description,
      details
    };
    const newHistory = [change, ...changeHistory].slice(0, 100); // Keep last 100 changes
    setChangeHistory(newHistory);
    localStorage.setItem('missing7hrChangeHistory', JSON.stringify(newHistory));
  };

  // Revert a change
  const revertChange = (change) => {
    if (!confirm(`Are you sure you want to revert this change?\n\n${change.description}`)) {
      return;
    }

    try {
      if (change.type === 'vacation') {
        // Revert vacation day change
        const { userId, date, action } = change.details;
        if (!userId || !date) {
          alert('Cannot revert: missing required information');
          return;
        }
        // Toggle the vacation day back (if it was added, remove it; if removed, add it)
        toggleVacationDay(userId, date);
        
      } else if (change.type === 'threshold') {
        // Revert threshold change
        const { userId, oldValue } = change.details;
        if (!userId) {
          alert('Cannot revert: missing user information');
          return;
        }
        // Restore the old threshold value
        if (oldValue === threshold || oldValue === undefined) {
          // If old value was the default, remove custom threshold
          removeCustomThreshold(userId);
        } else {
          // Otherwise set back to old value
          setCustomThreshold(userId, oldValue);
        }
        
      } else if (change.type === 'filter') {
        // Restore filter settings
        const details = change.details;
        if (details.dateRangeMode) setDateRangeMode(details.dateRangeMode);
        
        // Parse the period to extract dates
        if (details.period) {
          if (details.dateRangeMode === 'range' && details.period.includes(' to ')) {
            const [start, end] = details.period.split(' to ');
            setStartDate(start);
            setEndDate(end);
          } else if (details.dateRangeMode === 'month') {
            const [yearStr, monthStr] = details.period.split('-');
            if (yearStr && monthStr) {
              setYear(parseInt(yearStr));
              setMonth(parseInt(monthStr));
            }
          }
        }
        
        if (details.threshold !== undefined) setThreshold(details.threshold);
        if (details.workdaysOnly !== undefined) setWorkdaysOnly(details.workdaysOnly);
        
        alert('Filter settings restored. Click "Run Report" to apply them.');
      }
      
      // Remove this change from history after reverting
      const newHistory = changeHistory.filter(c => c.id !== change.id);
      setChangeHistory(newHistory);
      localStorage.setItem('missing7hrChangeHistory', JSON.stringify(newHistory));
      
      // Refresh the report if it's a vacation or threshold change
      if (change.type === 'vacation' || change.type === 'threshold') {
        fetchReport();
      }
      
    } catch (error) {
      console.error('Error reverting change:', error);
      alert('Failed to revert change: ' + error.message);
    }
  };

  // Save vacation days to localStorage
  const saveVacationDays = (newVacationDays) => {
    setVacationDays(newVacationDays);
    localStorage.setItem('userVacationDays', JSON.stringify(newVacationDays));
  };

  // Save user thresholds to localStorage
  const saveUserThresholds = (newThresholds) => {
    setUserThresholds(newThresholds);
    localStorage.setItem('userThresholds', JSON.stringify(newThresholds));
  };

  // Get threshold for a specific user (returns custom or default)
  const getUserThreshold = (userId) => {
    return userThresholds[userId] !== undefined ? userThresholds[userId] : threshold;
  };

  // Set custom threshold for a user
  const setCustomThreshold = (userId, customThreshold) => {
    const user = results.find(u => u.id === userId);
    const userName = user ? `${user.first_name} ${user.last_name}` : `User ${userId}`;
    const oldThreshold = userThresholds[userId];
    const newThresholds = { ...userThresholds, [userId]: parseFloat(customThreshold) };
    saveUserThresholds(newThresholds);
    logChange('threshold', `Set custom threshold for ${userName}`, {
      userId,
      userName,
      oldValue: oldThreshold || threshold,
      newValue: parseFloat(customThreshold)
    });
  };

  // Remove custom threshold for a user
  const removeCustomThreshold = (userId) => {
    const user = results.find(u => u.id === userId);
    const userName = user ? `${user.first_name} ${user.last_name}` : `User ${userId}`;
    const oldThreshold = userThresholds[userId];
    const newThresholds = { ...userThresholds };
    delete newThresholds[userId];
    saveUserThresholds(newThresholds);
    logChange('threshold', `Removed custom threshold for ${userName}`, {
      userId,
      userName,
      oldValue: oldThreshold,
      newValue: threshold
    });
  };

  // Get vacation days for a specific user and date range
  const getUserVacationDays = (userId) => {
    // For backward compatibility, check both old (year_month) and new (date range) keys
    if (dateRangeMode === 'month') {
      const key = `${userId}_${year}_${month}`;
      return vacationDays[key] || [];
    } else {
      // In date range mode, collect all vacation days within the range
      const allDays = [];
      const rangeStart = new Date(startDate);
      const rangeEnd = new Date(endDate);
      
      for (const key in vacationDays) {
        if (key.startsWith(`${userId}_`)) {
          const days = vacationDays[key];
          days.forEach(day => {
            const dayDate = new Date(day);
            if (dayDate >= rangeStart && dayDate <= rangeEnd) {
              allDays.push(day);
            }
          });
        }
      }
      return allDays;
    }
  };

  // Toggle vacation day for a user
  const toggleVacationDay = (userId, date) => {
    // Store vacation days by year-month to maintain compatibility
    const dateObj = new Date(date);
    const yearVal = dateObj.getFullYear();
    const monthVal = dateObj.getMonth() + 1;
    const key = `${userId}_${yearVal}_${monthVal}`;
    
    const currentDays = vacationDays[key] || [];
    const isAdding = !currentDays.includes(date);
    const newDays = currentDays.includes(date)
      ? currentDays.filter(d => d !== date)
      : [...currentDays, date];
    
    const newVacationDays = { ...vacationDays, [key]: newDays };
    saveVacationDays(newVacationDays);
    
    // Log the change
    const user = results.find(u => u.id === userId);
    const userName = user ? `${user.first_name} ${user.last_name}` : `User ${userId}`;
    logChange('vacation', `${isAdding ? 'Added' : 'Removed'} vacation day for ${userName}`, {
      userId,
      userName,
      date,
      action: isAdding ? 'add' : 'remove'
    });
  };

  // Export vacation data to JSON file
  const exportVacationData = () => {
    const data = {
      exportDate: new Date().toISOString(),
      version: '1.0',
      vacationDays: vacationDays,
      userThresholds: userThresholds,
      filters: {
        dateRangeMode,
        year,
        month,
        startDate,
        endDate,
        threshold,
        workdaysOnly,
        selectedGroupIds,
        filterByAdmin,
        filterByMember,
        selectedUserIds
      }
    };
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vacation-filters-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Import vacation data from JSON file
  const importVacationData = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result;
        const data = JSON.parse(content);
        
        if (data.vacationDays && typeof data.vacationDays === 'object') {
          // Merge with existing data or replace
          const shouldMerge = confirm('Do you want to merge with existing vacation data? Click OK to merge, Cancel to replace.');
          
          if (shouldMerge) {
            const merged = { ...vacationDays, ...data.vacationDays };
            saveVacationDays(merged);
          } else {
            saveVacationDays(data.vacationDays);
          }
          
          // Import user thresholds if available
          if (data.userThresholds && typeof data.userThresholds === 'object') {
            if (shouldMerge) {
              const mergedThresholds = { ...userThresholds, ...data.userThresholds };
              saveUserThresholds(mergedThresholds);
            } else {
              saveUserThresholds(data.userThresholds);
            }
          }
          
          // Import filters if available
          if (data.filters) {
            if (data.filters.dateRangeMode !== undefined) setDateRangeMode(data.filters.dateRangeMode);
            if (data.filters.year !== undefined) setYear(data.filters.year);
            if (data.filters.month !== undefined) setMonth(data.filters.month);
            if (data.filters.startDate !== undefined) setStartDate(data.filters.startDate);
            if (data.filters.endDate !== undefined) setEndDate(data.filters.endDate);
            if (data.filters.threshold !== undefined) setThreshold(data.filters.threshold);
            if (data.filters.workdaysOnly !== undefined) setWorkdaysOnly(data.filters.workdaysOnly);
            if (data.filters.selectedGroupIds !== undefined) setSelectedGroupIds(data.filters.selectedGroupIds);
            if (data.filters.filterByAdmin !== undefined) setFilterByAdmin(data.filters.filterByAdmin);
            if (data.filters.filterByMember !== undefined) setFilterByMember(data.filters.filterByMember);
            if (data.filters.selectedUserIds !== undefined) setSelectedUserIds(data.filters.selectedUserIds);
          }
          
          alert('Vacation data, user thresholds, and filters imported successfully!');
          fetchReport(); // Refresh to show updated data
        } else {
          alert('Invalid file format. Please select a valid vacation data backup file.');
        }
      } catch (error) {
        console.error('Error importing vacation data:', error);
        alert('Error importing file. Please make sure it is a valid JSON file.');
      }
    };
    reader.readAsText(file);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const fetchUserGroups = async () => {
    const sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) return;
    try {
      console.log('[Missing7hr] Fetching user groups...');
      const res = await fetch('/api/user-groups', { headers: { 'x-session-token': sessionId } });
      console.log('[Missing7hr] User groups response status:', res.status);
      if (res.ok) {
        const data = await res.json();
        console.log('[Missing7hr] User groups data:', data);
        setUserGroups(data);
      } else {
        const errorText = await res.text();
        console.error('[Missing7hr] Failed to fetch user groups:', res.status, errorText);
      }
    } catch (err) {
      console.error('[Missing7hr] Error fetching user groups:', err);
    }
  };

  const fetchUsersFromGroups = async () => {
    if (selectedGroupIds.length === 0) {
      setAvailableUsers([]);
      setSelectedUserIds([]);
      return;
    }
    
    const sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) return;
    
    try {
      const params = new URLSearchParams({ 
        userGroupIds: selectedGroupIds.join(','),
        filterByAdmin: String(filterByAdmin),
        filterByMember: String(filterByMember)
      });
      const res = await fetch(`/api/users-by-groups?${params.toString()}`, { 
        headers: { 'x-session-token': sessionId } 
      });
      
      if (res.ok) {
        const data = await res.json();
        setAvailableUsers(data.users || []);
        // Clear selected users when groups change
        setSelectedUserIds([]);
      } else {
        console.error('Failed to fetch users from groups');
        setAvailableUsers([]);
        setSelectedUserIds([]);
      }
    } catch (err) {
      console.error('Error fetching users from groups:', err);
      setAvailableUsers([]);
      setSelectedUserIds([]);
    }
  };

  const fetchReport = async () => {
    const sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      setError('Not connected. Please connect first.');
      return;
    }
    
    // Log filter change
    const filterDesc = dateRangeMode === 'range' 
      ? `${startDate} to ${endDate}`
      : `${year}-${String(month).padStart(2, '0')}`;
    logChange('filter', `Report run with filters`, {
      dateRangeMode,
      period: filterDesc,
      threshold,
      workdaysOnly,
      groupCount: selectedGroupIds.length,
      userCount: selectedUserIds.length
    });
    
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ 
        threshold: String(threshold), 
        workdaysOnly: String(workdaysOnly),
        ...(selectedGroupIds.length > 0 && { userGroupIds: selectedGroupIds.join(',') }),
        ...(selectedUserIds.length > 0 && { userIds: selectedUserIds.join(',') }),
        ...(filterByAdmin && { filterByAdmin: 'true' }),
        ...(filterByMember && { filterByMember: 'true' })
      });
      
      // Add date parameters based on mode
      if (dateRangeMode === 'range') {
        params.append('startDate', startDate);
        params.append('endDate', endDate);
      } else {
        params.append('year', String(year));
        params.append('month', String(month));
      }
      const res = await fetch(`/api/worklogs/missing-7hr?${params.toString()}`, { headers: { 'x-session-token': sessionId } });
      if (res.status === 401) {
        setError('Authentication error. Please reconnect.');
        setLoading(false);
        return;
      }
      const data = await res.json();
      if (data.error) throw new Error(data.error || data.details || 'API error');
      
      // Apply user-specific thresholds and filter out vacation days
      const resultsWithVacations = (data.users || []).map(user => {
        const userVacations = getUserVacationDays(user.id);
        const userCustomThreshold = getUserThreshold(user.id);
        
        // Recalculate missing days based on user-specific threshold
        const recalculatedMissingDays = user.missingDaysDetails.filter(day => {
          // Skip vacation days
          if (userVacations.includes(day.date)) return false;
          // Apply user-specific threshold
          return day.totalHours < userCustomThreshold;
        });
        
        return {
          ...user,
          missingDaysDetails: recalculatedMissingDays,
          missingCount: recalculatedMissingDays.length,
          missingDays: recalculatedMissingDays.map(d => d.date),
          customThreshold: userThresholds[user.id],
          vacationDays: userVacations.filter(vd => {
            // Only include vacation days that were in the original missing days
            return user.missingDaysDetails.some(md => md.date === vd);
          })
        };
      }).filter(user => user.missingCount > 0 || (user.vacationDays && user.vacationDays.length > 0)); // Show users with missing days or vacation days
      
      setResults(resultsWithVacations);
    } catch (err) {
      console.error('Failed to fetch report', err);
      setError(err.message);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch user groups and report on mount
    fetchUserGroups();
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Fetch users when groups or filters change
    fetchUsersFromGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroupIds, filterByAdmin, filterByMember]);

  useEffect(() => {
    // Save filters to localStorage whenever they change
    const filters = {
      dateRangeMode,
      year,
      month,
      startDate,
      endDate,
      threshold,
      workdaysOnly,
      selectedGroupIds,
      filterByAdmin,
      filterByMember,
      selectedUserIds
    };
    localStorage.setItem('missing7hrFilters', JSON.stringify(filters));
  }, [dateRangeMode, year, month, startDate, endDate, threshold, workdaysOnly, selectedGroupIds, filterByAdmin, filterByMember, selectedUserIds]);

  const exportCSV = () => {
    const headers = ['id', 'first_name', 'last_name', 'username', 'is_active', 'missingCount', 'vacationDaysCount', 'expectedDays', 'date', 'type', 'totalHours', 'logs'];
    const rows = [];
    results.forEach(r => {
      // Export missing days
      if (r.missingDaysDetails && r.missingDaysDetails.length > 0) {
        r.missingDaysDetails.forEach(day => {
          const logsStr = day.logs.map(l => `${l.summary} (${l.hours.toFixed(2)}h)`).join('; ');
          rows.push([
            r.id, 
            r.first_name, 
            r.last_name, 
            r.username || '', 
            r.is_active === false ? 'Inactive' : 'Active',
            r.missingCount, 
            r.vacationDays?.length || 0,
            r.expectedDays, 
            day.date, 
            'Missing',
            day.totalHours.toFixed(2), 
            logsStr
          ]);
        });
      }
      // Export vacation days
      if (r.vacationDays && r.vacationDays.length > 0) {
        r.vacationDays.forEach(vd => {
          rows.push([
            r.id, 
            r.first_name, 
            r.last_name, 
            r.username || '', 
            r.is_active === false ? 'Inactive' : 'Active',
            r.missingCount, 
            r.vacationDays.length,
            r.expectedDays, 
            vd, 
            'Vacation',
            '', 
            ''
          ]);
        });
      }
      // If no missing or vacation days, add one row
      if ((!r.missingDaysDetails || r.missingDaysDetails.length === 0) && (!r.vacationDays || r.vacationDays.length === 0)) {
        rows.push([r.id, r.first_name, r.last_name, r.username || '', r.is_active === false ? 'Inactive' : 'Active', r.missingCount, 0, r.expectedDays, '', '', '', '']);
      }
    });
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const filename = dateRangeMode === 'range' 
      ? `missing-7hr-${startDate}_to_${endDate}.csv`
      : `missing-7hr-${year}-${String(month).padStart(2, '0')}.csv`;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const toggleUserExpanded = (userId) => {
    const newExpanded = new Set(expandedUsers);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedUsers(newExpanded);
  };

  return (
    <div className="h-full flex">
      {/* Filters Sidebar */}
      <div className="w-80 bg-white border-r shadow-sm overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-bold mb-2">Filters</h2>
          <p className="text-sm text-gray-500 mb-6">
            {results.length} users with missing days
          </p>

          <div className="space-y-6">
            {/* Date Parameters */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold">Report Period</Label>
              
              {/* Mode Selection */}
              <div className="flex gap-2 mb-3">
                <Button
                  onClick={() => setDateRangeMode('month')}
                  variant={dateRangeMode === 'month' ? 'default' : 'outline'}
                  className="flex-1"
                  size="sm"
                >
                  Month
                </Button>
                <Button
                  onClick={() => setDateRangeMode('range')}
                  variant={dateRangeMode === 'range' ? 'default' : 'outline'}
                  className="flex-1"
                  size="sm"
                >
                  Date Range
                </Button>
              </div>
              
              {dateRangeMode === 'month' ? (
                <>
                  <div>
                    <Label className="text-xs text-gray-600">Year</Label>
                    <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Month</Label>
                    <Input type="number" min={1} max={12} value={month} onChange={(e) => setMonth(Number(e.target.value))} className="mt-1" />
                  </div>
                </>
              ) : (
                <>
                  {/* Quick Select: Last 30 Days */}
                  <Button
                    onClick={() => {
                      const end = new Date();
                      const start = new Date();
                      start.setDate(start.getDate() - 30);
                      setStartDate(formatLocalDate(start));
                      setEndDate(formatLocalDate(end));
                    }}
                    variant="secondary"
                    className="w-full bg-purple-100 text-purple-700 hover:bg-purple-200 border-purple-300"
                    size="sm"
                  >
                    📅 Last 30 Days
                  </Button>
                  <div>
                    <Label className="text-xs text-gray-600">Start Date</Label>
                    <Input 
                      type="date" 
                      value={startDate} 
                      onChange={(e) => setStartDate(e.target.value)} 
                      className="mt-1" 
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">End Date</Label>
                    <Input 
                      type="date" 
                      value={endDate} 
                      onChange={(e) => setEndDate(e.target.value)} 
                      className="mt-1" 
                    />
                  </div>
                </>
              )}
              
              <div>  
                <Label className="text-xs text-gray-600">Threshold (hours)</Label>
                <Input type="number" min={0} step={0.1} value={threshold} onChange={(e) => setThreshold(Number(e.target.value))} className="mt-1" />
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Checkbox 
                  id="workdays-only"
                  checked={workdaysOnly} 
                  onCheckedChange={setWorkdaysOnly}
                />
                <Label htmlFor="workdays-only" className="text-sm font-normal cursor-pointer">Workdays only</Label>
              </div>
            </div>

            {/* User Groups */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">User Groups</Label>
              <div className="border rounded p-2 max-h-48 overflow-y-auto bg-gray-50">
                {userGroups.length === 0 ? (
                  <div className="text-sm text-gray-400 py-1">No groups available</div>
                ) : (
                  userGroups.map(g => (
                    <label key={g.id} className="flex items-center gap-2 py-1.5 hover:bg-white px-2 rounded cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={selectedGroupIds.includes(String(g.id))}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedGroupIds([...selectedGroupIds, String(g.id)]);
                          } else {
                            setSelectedGroupIds(selectedGroupIds.filter(id => id !== String(g.id)));
                          }
                        }}
                        className="rounded"
                      />
                      <span className="text-sm">{g.name || `Group ${g.id}`}</span>
                    </label>
                  ))
                )}
              </div>
              {selectedGroupIds.length > 0 && (
                <div className="space-y-2 mt-3 pt-3 border-t">
                  <Label className="text-xs text-gray-600">Include from groups:</Label>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="filter-admin"
                      checked={filterByAdmin} 
                      onCheckedChange={setFilterByAdmin}
                    />
                    <Label htmlFor="filter-admin" className="text-sm font-normal cursor-pointer">Group Admins</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="filter-member"
                      checked={filterByMember} 
                      onCheckedChange={setFilterByMember}
                    />
                    <Label htmlFor="filter-member" className="text-sm font-normal cursor-pointer">Group Members</Label>
                  </div>
                </div>
              )}
            </div>

            {/* Specific Users */}
            {availableUsers.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Specific Users</Label>
                <p className="text-xs text-gray-500">Leave empty to show all users from selected groups</p>
                <div className="border rounded bg-gray-50">
                  <div className="flex gap-2 p-2 border-b bg-white">
                    <Button 
                      onClick={() => setSelectedUserIds(availableUsers.map(u => String(u.id)))}
                      size="sm"
                      variant="default"
                    >
                      All
                    </Button>
                    <Button 
                      onClick={() => setSelectedUserIds([])}
                      size="sm"
                      variant="secondary"
                    >
                      None
                    </Button>
                  </div>
                  <div className="max-h-64 overflow-y-auto p-2">
                    {availableUsers.map(u => (
                      <div key={u.id} className="flex items-center gap-2 py-1.5 hover:bg-white px-2 rounded">
                        <Checkbox 
                          id={`user-${u.id}`}
                          checked={selectedUserIds.includes(String(u.id))}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedUserIds([...selectedUserIds, String(u.id)]);
                            } else {
                              setSelectedUserIds(selectedUserIds.filter(id => id !== String(u.id)));
                            }
                          }}
                        />
                        <Label 
                          htmlFor={`user-${u.id}`}
                          className="text-sm flex-1 font-normal cursor-pointer"
                        >
                          {u.first_name} {u.last_name}
                        </Label>
                        {u.is_active === false && (
                          <Badge variant="secondary" className="text-xs">
                            Inactive
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Run Button */}
            <Button 
              onClick={fetchReport} 
              className="w-full"
            >
              Run Report
            </Button>

            {/* Vacation Data Management */}
            <div className="space-y-2 pt-6 border-t">
              <Label className="text-sm font-semibold">Backup & Restore</Label>
              <p className="text-xs text-gray-500 mb-2">
                Export vacation days and filter settings to avoid losing them
              </p>
              
              <Button 
                onClick={exportVacationData}
                variant="secondary"
                className="w-full bg-green-100 text-green-700 hover:bg-green-200"
                size="sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Export All Settings
              </Button>
              
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={importVacationData}
                className="hidden"
              />
              
              <Button 
                onClick={() => fileInputRef.current?.click()}
                variant="secondary"
                className="w-full bg-orange-100 text-orange-700 hover:bg-orange-200"
                size="sm"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import Settings
              </Button>
            </div>

            {/* Change History */}
            <div className="space-y-2 pt-6 border-t">
              <Label className="text-sm font-semibold">Change History</Label>
              <p className="text-xs text-gray-500 mb-2">
                View all modifications to vacation days, thresholds, and filters
              </p>
              
              <Button 
                onClick={() => setShowChangeHistory(true)}
                variant="secondary"
                className="w-full bg-blue-100 text-blue-700 hover:bg-blue-200"
                size="sm"
              >
                <Calendar className="w-4 h-4 mr-2" />
                View Changes ({changeHistory.length})
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col bg-gray-50">
        <div className="p-6 border-b bg-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Monthly 7hr Missing Report</h1>
              <p className="text-sm text-gray-600 mt-1">
                Users who did not log the threshold hours per expected day
              </p>
            </div>
            <Button 
              onClick={exportCSV} 
              disabled={results.length === 0} 
              variant="secondary"
              className="bg-gray-100 hover:bg-gray-200"
            >
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          </div>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
            {error}
          </div>
        )}
        
        {loading && (
          <div className="mx-6 mt-4 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded">
            Loading report...
          </div>
        )}

        <div className="flex-1 m-6 overflow-hidden flex flex-col bg-white border rounded shadow-sm">
          <div className="flex-1 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left border-b w-8"></th>
                  <th className="px-3 py-2 text-left border-b">User</th>
                  <th className="px-3 py-2 text-left border-b w-20">Active</th>
                  <th className="px-3 py-2 text-left border-b">Threshold</th>
                  <th className="px-3 py-2 text-left border-b">Missing Days</th>
                  <th className="px-3 py-2 text-left border-b w-24">Vacation Days</th>
                  <th className="px-3 py-2 text-left border-b">Expected Days</th>
                  <th className="px-3 py-2 text-left border-b w-28">Actions</th>
                </tr>
              </thead>
              <tbody>
                {results.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-center text-gray-500">
                      {loading ? 'Loading...' : 'No users with missing days. Run the report to see results.'}
                    </td>
                  </tr>
                ) : (
                  results.map(u => (
                <>
                  <tr key={u.id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => toggleUserExpanded(u.id)}>
                    <td className="px-3 py-2">
                      {expandedUsers.has(u.id) ? (
                        <ChevronDown className="w-4 h-4 text-gray-600" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-600" />
                      )}
                    </td>
                    <td className="px-3 py-2">{u.first_name} {u.last_name} {u.username ? `(${u.username})` : ''}</td>
                    <td className="px-3 py-2">
                      {u.is_active === false ? (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded font-medium">Inactive</span>
                      ) : (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-medium">Active</span>
                      )}
                    </td>
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      {editingThresholdUserId === u.id ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            min="1"
                            max="24"
                            step="0.5"
                            value={userThresholds[u.id] ?? threshold}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value);
                              if (!isNaN(val) && val > 0) {
                                setCustomThreshold(u.id, val);
                              }
                            }}
                            className="w-16 px-1 py-0.5 text-sm border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            autoFocus
                          />
                          <Button
                            onClick={() => setEditingThresholdUserId(null)}
                            size="sm"
                            variant="secondary"
                            className="h-6 px-2 bg-green-100 text-green-700 hover:bg-green-200"
                          >
                            ✓
                          </Button>
                          {userThresholds[u.id] !== undefined && (
                            <Button
                              onClick={() => {
                                removeCustomThreshold(u.id);
                                setEditingThresholdUserId(null);
                              }}
                              size="sm"
                              variant="destructive"
                              className="h-6 px-2"
                            >
                              Reset
                            </Button>
                          )}
                        </div>
                      ) : (
                        <Button
                          onClick={() => setEditingThresholdUserId(u.id)}
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                        >
                          {userThresholds[u.id] !== undefined ? (
                            <span className="font-medium text-blue-600">{userThresholds[u.id]}h ⚙</span>
                          ) : (
                            <span className="text-gray-600">{threshold}h</span>
                          )}
                        </Button>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className="font-medium">{u.missingCount}</span>
                      {u.missingDaysDetails && u.missingDaysDetails.length > 0 && (
                        <span className="text-xs text-gray-500 ml-2">
                          ({u.missingDaysDetails.map(d => d.date).join(', ')})
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {u.vacationDays && u.vacationDays.length > 0 ? (
                        <span className="text-sm text-blue-600 flex items-center gap-1">
                          <Plane className="w-3 h-3" />
                          {u.vacationDays.length}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2">{u.expectedDays}</td>
                    <td className="px-3 py-2">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          setVacationUserId(u.id);
                          setVacationUserName(`${u.first_name} ${u.last_name}`);
                          setShowVacationModal(true);
                        }}
                        size="sm"
                        variant="secondary"
                        className="bg-blue-100 text-blue-700 hover:bg-blue-200"
                      >
                        <Calendar className="w-3 h-3 mr-1" />
                        Manage
                      </Button>
                    </td>
                  </tr>
                  {expandedUsers.has(u.id) && u.missingDaysDetails && u.missingDaysDetails.length > 0 && (
                    <tr key={`${u.id}-details`} className="bg-gray-50">
                      <td colSpan={8} className="px-3 py-2">
                        <div className="pl-8">
                          <h4 className="font-semibold text-xs text-gray-700 mb-2">Missing Days Details:</h4>
                          {u.missingDaysDetails.map((day, idx) => {
                            const userCustomThreshold = getUserThreshold(u.id);
                            return (
                            <div key={idx} className="mb-3 border-l-2 border-blue-300 pl-3">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-gray-700">{day.date}</span>
                                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                                  {day.totalHours.toFixed(2)}h / {userCustomThreshold}h
                                </span>
                              </div>
                              {day.logs && day.logs.length > 0 ? (
                                <div className="space-y-1">
                                  {day.logs.map((log, logIdx) => (
                                    <div key={logIdx} className="text-xs text-gray-600 bg-white px-2 py-1 rounded">
                                      <span className="font-medium text-blue-600">{log.hours.toFixed(2)}h</span>
                                      <span className="mx-2">-</span>
                                      <span>{log.summary}</span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-xs text-gray-500 italic">No logs recorded</div>
                              )}
                            </div>
                          )})}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))
            )}
            </tbody>
          </table>
        </div>
      </div>
    </div>

      {/* Vacation Management Modal */}
      {showVacationModal && vacationUserId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between bg-blue-50">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-semibold">Manage Vacation Days - {vacationUserName}</h3>
              </div>
              <Button
                onClick={() => {
                  setShowVacationModal(false);
                  setVacationUserId(null);
                  setVacationUserName('');
                  fetchReport(); // Refresh the report to show updated vacation days
                }}
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-4 overflow-y-auto flex-1">
              <p className="text-sm text-gray-600 mb-4">
                Click on any missing day below to mark it as vacation. Vacation days will be excluded from the missing days count.
              </p>
              
              <div className="space-y-2">
                {/* Get the user's data from results */}
                {(() => {
                  const user = results.find(u => u.id === vacationUserId);
                  if (!user) return <div className="text-gray-500">No data available</div>;
                  
                  const userVacations = getUserVacationDays(vacationUserId);
                  const allPotentialDays = [...(user.missingDaysDetails || []), ...(user.vacationDays || []).map(vd => ({ date: vd, totalHours: 0, logs: [] }))];
                  const uniqueDays = Array.from(new Map(allPotentialDays.map(d => [d.date, d])).values()).sort((a, b) => a.date.localeCompare(b.date));
                  
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {uniqueDays.map(day => {
                        const isVacation = userVacations.includes(day.date);
                        const isMissing = user.missingDaysDetails.some(md => md.date === day.date);
                        
                        return (
                          <Button
                            key={day.date}
                            onClick={() => toggleVacationDay(vacationUserId, day.date)}
                            variant="outline"
                            className={`p-3 h-auto justify-start text-left transition-all ${
                              isVacation
                                ? 'border-blue-500 bg-blue-50 hover:bg-blue-100'
                                : isMissing
                                ? 'border-red-200 bg-red-50 hover:bg-red-100'
                                : 'border-gray-200 bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm">{day.date}</span>
                              {isVacation ? (
                                <span className="flex items-center gap-1 text-xs text-blue-600 font-medium">
                                  <Plane className="w-3 h-3" />
                                  Vacation
                                </span>
                              ) : isMissing ? (
                                <span className="text-xs text-red-600 font-medium">Missing</span>
                              ) : null}
                            </div>
                            <div className="text-xs text-gray-600">
                              {day.totalHours > 0 ? `${day.totalHours.toFixed(2)} hrs logged` : 'No logs'}
                            </div>
                            {day.logs && day.logs.length > 0 && (
                              <div className="mt-1 text-xs text-gray-500">
                                {day.logs.length} worklog{day.logs.length > 1 ? 's' : ''}
                              </div>
                            )}
                          </Button>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
              
              <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm">
                <div className="flex gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-blue-500 rounded"></div>
                    <span>Vacation Day</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-red-200 rounded"></div>
                    <span>Missing Day (can mark as vacation)</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
              <Button
                onClick={() => {
                  setShowVacationModal(false);
                  setVacationUserId(null);
                  setVacationUserName('');
                  fetchReport();
                }}
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Change History Modal */}
      {showChangeHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-xl font-bold">Change History</h2>
              <Button 
                onClick={() => setShowChangeHistory(false)}
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="flex-1 overflow-auto p-4">
              {changeHistory.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  No changes recorded yet
                </div>
              ) : (
                <div className="space-y-2">
                  {changeHistory.map((change) => {
                    const date = new Date(change.timestamp);
                    const typeColors = {
                      vacation: 'bg-purple-100 text-purple-700 border-purple-200',
                      threshold: 'bg-blue-100 text-blue-700 border-blue-200',
                      filter: 'bg-green-100 text-green-700 border-green-200'
                    };
                    const typeIcons = {
                      vacation: '🏖️',
                      threshold: '⚙️',
                      filter: '🔍'
                    };
                    
                    return (
                      <div key={change.id} className={`p-3 border rounded ${typeColors[change.type] || 'bg-gray-100'}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-lg">{typeIcons[change.type]}</span>
                              <span className="font-medium text-sm capitalize">{change.type}</span>
                              <span className="text-xs opacity-75">
                                {date.toLocaleString()}
                              </span>
                            </div>
                            <div className="text-sm">{change.description}</div>
                            {change.details && Object.keys(change.details).length > 0 && (
                              <div className="mt-2 text-xs space-y-1">
                                {change.details.date && (
                                  <div>📅 Date: <span className="font-mono">{change.details.date}</span></div>
                                )}
                                {change.details.oldValue !== undefined && (
                                  <div>Old: <span className="font-mono">{change.details.oldValue}</span> → New: <span className="font-mono">{change.details.newValue}</span></div>
                                )}
                                {change.details.period && (
                                  <div>📊 Period: {change.details.period}</div>
                                )}
                                {change.details.threshold && (
                                  <div>⏱️ Threshold: {change.details.threshold}hrs</div>
                                )}
                                {change.details.workdaysOnly !== undefined && (
                                  <div>📅 Workdays Only: {change.details.workdaysOnly ? 'Yes' : 'No'}</div>
                                )}
                                {change.details.groupCount !== undefined && (
                                  <div>👥 Groups: {change.details.groupCount}</div>
                                )}
                                {change.details.userCount !== undefined && (
                                  <div>👤 Specific Users: {change.details.userCount || 'All'}</div>
                                )}
                              </div>
                            )}
                          </div>
                          {(change.type === 'vacation' || change.type === 'threshold' || change.type === 'filter') && (
                            <Button
                              onClick={() => revertChange(change)}
                              size="sm"
                              variant="ghost"
                              className="border"
                              title="Revert this change"
                            >
                              ↶ Undo
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div className="p-4 border-t flex justify-between">
              <Button
                onClick={() => {
                  if (confirm('Are you sure you want to clear all change history? This cannot be undone.')) {
                    setChangeHistory([]);
                    localStorage.removeItem('missing7hrChangeHistory');
                  }
                }}
                variant="destructive"
              >
                Clear History
              </Button>
              <Button
                onClick={() => setShowChangeHistory(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
